import { useEffect } from "react";

/**
 * Hook that sets a fixed viewport height CSS variable (--vh) to prevent
 * layout jumping on mobile when the address bar shows/hides.
 *
 * Strategy: Wait briefly after load for address bar to potentially hide,
 * then capture the MAXIMUM viewport height seen. This ensures content
 * fills the screen when the address bar is hidden (most of scroll time).
 *
 * Note: CSS now uses svh (small viewport height) which is stable.
 * This hook is kept for JavaScript calculations that need stable viewport height
 * (e.g., GSAP animations in usePlatformFeaturesAnimation.ts).
 *
 * Usage in JS: getComputedStyle(document.documentElement).getPropertyValue('--vh')
 */
export const useFixedViewportHeight = () => {
  useEffect(() => {
    let maxVh = 0;

    const setVh = () => {
      const currentVh = window.innerHeight * 0.01;
      // Only update if we see a larger viewport (address bar hidden)
      if (currentVh > maxVh) {
        maxVh = currentVh;
        document.documentElement.style.setProperty("--vh", `${maxVh}px`);
      }
    };

    // Set initial value
    setVh();

    // Check again after a short delay - address bar might hide after initial load
    const timeoutId = setTimeout(setVh, 300);

    // Listen for resize briefly to catch the larger viewport
    const handleResize = () => setVh();
    window.addEventListener("resize", handleResize);

    // Stop listening after 2 seconds - we should have the max value by then
    const cleanupTimeoutId = setTimeout(() => {
      window.removeEventListener("resize", handleResize);
    }, 2000);

    // Only update on orientation change after initial capture
    const handleOrientationChange = () => {
      maxVh = 0; // Reset max for new orientation
      setTimeout(setVh, 100);
    };

    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(cleanupTimeoutId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);
};
